// JOBE-0168: EntityBrowserRail — left-column rail for the top-level
// `/entities` Entity Browser route. Three blocks, stacked top-to-bottom:
//
//   1. Saved views (top): two built-in chip presets —
//        - "Mine, this week"  (owner:me since:last-7d)
//        - "Recent activity"  (since:last-7d)
//      No user-saved views in v1 (per spec §Out of scope).
//
//   2. Type block (middle): flat list from EntityTypesContext. Document
//      sub-kinds (those with `structuralShape === 'document'` and/or a
//      `parent_kind`) are grouped under a "Document" header; non-document
//      kinds are top-level rows. No "All documents" shortcut row — the
//      Library is the home of that.
//
//   3. Register-new-kind affordance (bottom): links into the ADR-0015
//      entity-type registration flow. Placeholder anchor today; lights
//      up when the ontology surface lands.
//
// No Collections block (Collections are a document-centric concept; the
// Library owns that rail).
//
// Reads chip state from the host's <EntityBrowserContext> so a click
// on a saved view or type row dispatches via the host's setChips/setView
// rather than spinning up a duplicate URL-state hook.

import { useCallback, useContext, useMemo } from 'react';
import { EntityTypesContext } from '../library/EntityTypesContext.js';
import { FRIENDLY_TOKENS, makeScopeChip } from '../library/chipVocabulary.js';
import { RailRow } from '../library/RailRow.jsx';
import { useEntityBrowserContext } from './EntityBrowserContext.js';

// ─── Built-in saved views (Entity Browser specific) ───────────────────

// The two built-ins for the Entity Browser. Hard-coded chip presets
// rather than the Library's queryTemplate registry (the Library views
// are document-focused; Entity Browser views span all kinds).
function daysAgoIso(days) {
  return new Date(Date.now() - days * 86_400_000).toISOString();
}

function predicateChip(friendlyToken, values) {
  const entry = FRIENDLY_TOKENS[friendlyToken];
  return {
    kind: 'predicate',
    friendlyToken,
    wirePredicate: entry?.wirePredicate,
    values,
  };
}

export const ENTITY_BROWSER_SAVED_VIEWS = [
  {
    id: 'mine-this-week',
    glyph: '★',
    label: 'Mine, this week',
    description: 'Entities you own from the last 7 days.',
    resolve: ({ principalUrn } = {}) => {
      const chips = [makeScopeChip()];
      if (principalUrn) {
        chips.push(predicateChip('owner', [principalUrn]));
      } else {
        // No principal wired today — emit the literal 'me' sentinel so
        // the URL still carries the intent and a future principal-aware
        // resolver can rehydrate.
        chips.push(predicateChip('owner', ['me']));
      }
      chips.push(predicateChip('since', [daysAgoIso(7)]));
      return chips;
    },
  },
  {
    id: 'recent-activity',
    glyph: '·',
    label: 'Recent activity',
    description: 'Everything changed in the last 7 days, all kinds.',
    resolve: () => [
      makeScopeChip(),
      predicateChip('since', [daysAgoIso(7)]),
    ],
  },
];

// ─── SavedViewsBlock (Entity Browser variant) ─────────────────────────

function SavedViewsBlock({ activeView, onPickView, principalUrn }) {
  return (
    <section className="library-rail-block" aria-label="Saved views">
      <header className="library-rail-block-header">
        <h3 className="library-rail-block-title">Saved views</h3>
      </header>
      <ul className="library-rail-block-list">
        {ENTITY_BROWSER_SAVED_VIEWS.map((view) => (
          <li key={view.id}>
            <RailRow
              glyph={view.glyph}
              label={view.label}
              title={view.description}
              selected={activeView === view.id}
              onClick={() => onPickView(view)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

// ─── TypeBlock (Entity Browser variant) ───────────────────────────────

// isDocumentSubKind: a kind belongs under the "Document" group when it
// either declares `structuralShape === 'document'` (legacy seam) OR
// declares a `parent_kind` of `document` (ADR-0026's hierarchical seam).
// Top-level `document` itself is NOT a sub-kind — it's the group anchor.
function isDocumentSubKind(t) {
  if (!t) return false;
  if (t.parent_kind === 'document') return true;
  // structuralShape: 'document' means "shaped like a document" — the
  // top-level `document` kind itself carries this too. We group only
  // sub-kinds (non-top-level) under the Document header.
  if (t.structuralShape === 'document' && t.kind !== 'document') return true;
  return false;
}

function TypeBlock({ activeType, onPickType }) {
  const ctx = useContext(EntityTypesContext);

  // Partition entity types: document sub-kinds vs non-document.
  // The top-level `document` kind (kind === 'document') is suppressed from
  // otherKinds when sub-kinds exist — it would be redundant with the group
  // header that already carries the "Document" label.
  const { docKinds, otherKinds } = useMemo(() => {
    const types = Array.isArray(ctx?.types) ? ctx.types : [];
    const docs = [];
    const others = [];
    for (const t of types) {
      if (isDocumentSubKind(t)) {
        docs.push(t);
      } else {
        others.push(t);
      }
    }
    const filteredOthers = docs.length > 0
      ? others.filter((t) => t.kind !== 'document')
      : others;
    return { docKinds: docs, otherKinds: filteredOthers };
  }, [ctx?.types]);

  if (ctx?.status === 'loading') {
    return (
      <section className="library-rail-block" aria-label="Type rail">
        <header className="library-rail-block-header">
          <h3 className="library-rail-block-title">Types</h3>
        </header>
        <ul className="library-rail-block-list" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <li key={i}>
              <div className="library-rail-row library-rail-row-skeleton" aria-hidden="true">
                <span className="skel-line skel-w-80" />
              </div>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  if (ctx?.status === 'error') {
    return (
      <section className="library-rail-block" aria-label="Type rail">
        <header className="library-rail-block-header">
          <h3 className="library-rail-block-title">Types</h3>
        </header>
        <p className="library-rail-block-error">couldn't load</p>
      </section>
    );
  }

  return (
    <section className="library-rail-block" aria-label="Type rail">
      <header className="library-rail-block-header">
        <h3 className="library-rail-block-title">Types</h3>
      </header>
      {otherKinds.length === 0 && docKinds.length === 0 && (
        <p className="library-rail-block-empty">nothing</p>
      )}
      {(otherKinds.length > 0 || docKinds.length > 0) && (
        <ul className="library-rail-block-list">
          {otherKinds.map((t) => (
            <li key={t.kind}>
              <RailRow
                glyph="·"
                label={t.displayName || t.kind}
                pill={t.kindSource ? { kind: t.kindSource, label: t.kindSource } : null}
                count={t.count}
                selected={activeType === t.kind}
                onClick={() => onPickType(t.kind)}
              />
            </li>
          ))}
          {docKinds.length > 0 && (
            <li className="library-rail-block-group">
              <div
                className="library-rail-block-group-header"
                data-testid="entity-browser-rail-document-header"
              >
                Document
              </div>
              <ul className="library-rail-block-list library-rail-block-sublist">
                {docKinds.map((t) => (
                  <li key={t.kind}>
                    <RailRow
                      glyph="·"
                      label={t.displayName || t.kind}
                      pill={t.kindSource ? { kind: t.kindSource, label: t.kindSource } : null}
                      count={t.count}
                      selected={activeType === t.kind}
                      onClick={() => onPickType(t.kind)}
                    />
                  </li>
                ))}
              </ul>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

// ─── RegisterKindBlock ─────────────────────────────────────────────────
// Footer affordance — links into the ADR-0015 entity-type registration
// flow. Placeholder anchor today; the real registration surface ships
// with the ontology surface work.

function RegisterKindBlock() {
  return (
    <section className="library-rail-block" aria-label="Register entity type">
      <button
        type="button"
        className="library-rail-block-register"
        title="Register a new entity type (ADR-0015 — coming with the ontology surface)"
        data-testid="entity-browser-rail-register-kind"
        disabled
      >
        + Register entity type
      </button>
    </section>
  );
}

// ─── EntityBrowserRail (host) ─────────────────────────────────────────

export function EntityBrowserRail({ principalUrn = null }) {
  const { chips, view, setChips, setView } = useEntityBrowserContext();

  // Derive the currently-active `type:` chip value (if exactly one) so
  // the type rail can highlight the matching row.
  const activeType = useMemo(() => {
    const c = (chips || []).find(
      (chip) => chip.kind === 'predicate' && chip.friendlyToken === 'type',
    );
    if (!c || !Array.isArray(c.values) || c.values.length !== 1) return null;
    return c.values[0];
  }, [chips]);

  // Saved-view click: write `?view=<id>` and replace the chip set with
  // the resolved chips. Mirrors the Library's onPickView behaviour.
  const onPickView = useCallback((pickedView) => {
    if (!pickedView) return;
    const resolvedChips = pickedView.resolve({ principalUrn });
    setView(pickedView.id);
    if (Array.isArray(resolvedChips)) {
      setChips(resolvedChips);
    }
  }, [setView, setChips, principalUrn]);

  // Type-rail click: single-select per Library's R3. Clicking the same
  // type clears the filter.
  const onPickType = useCallback((kind) => {
    setView(null);
    setChips((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const withoutType = list.filter(
        (c) => !(c.kind === 'predicate' && c.friendlyToken === 'type'),
      );
      // Toggle off when clicking the active type.
      const wasActive = list.some(
        (c) => c.kind === 'predicate'
          && c.friendlyToken === 'type'
          && Array.isArray(c.values) && c.values.length === 1
          && c.values[0] === kind,
      );
      if (wasActive || !kind) return withoutType;
      return [...withoutType, {
        kind: 'predicate',
        friendlyToken: 'type',
        wirePredicate: FRIENDLY_TOKENS.type.wirePredicate,
        values: [kind],
      }];
    });
  }, [setChips, setView]);

  return (
    <div className="library-rail-stack" data-testid="entity-browser-rail">
      <SavedViewsBlock
        activeView={view}
        onPickView={onPickView}
        principalUrn={principalUrn}
      />
      <TypeBlock activeType={activeType} onPickType={onPickType} />
      <RegisterKindBlock />
    </div>
  );
}
